<template>
    <page-loading v-if="loading" class="col-span-12 lg:col-span-10"></page-loading>
    <div v-else class="col-span-12 lg:col-span-10">
        <step-form-heading v-if="!props.campaignId" :icon="faFlagSwallowtail">
            New Campaign

            <template v-slot:description>
                Create blast and evergreen campaigns.
            </template>
        </step-form-heading>

        <form-wrapper
                :sections="[
                    { id: 'name', label: 'Name' },
                    { id: 'category', label: 'Category' },
                    { id: 'audience_type', label: 'Audience' },
                    { id: 'when', label: 'When' },
                    { id: 'what', label: 'What' },
                    { id: 'to', label: 'To' },
                ]"
                :hideNav="!!props.campaignId"
            >
                <div v-if="campaign?.flow_id">
                    <flow-resource-state-alert
                        ref="flowAlertRef"
                        :resourceName="campaign?.name"
                        :editing="!!campaign?.id"
                        :flowId="campaign?.flow_id"
                        :flowSection="campaign?.flow_section">
                    </flow-resource-state-alert>
                </div>

                <template v-if="!props.campaignId || campaign?.id">

                    <campaign-step-form-name id="name"
                        v-model="campaign.name"
                        :error="displayFirstError(validation.name.$errors)"></campaign-step-form-name>
                    <resource-category
                        id="category"
                        v-model="campaign.category"
                        @reset="campaign.category = undefined"
                        :disabled="!!campaign.flow_id">
                    </resource-category>
                    <campaign-step-form-audience-type
                        id="audience_type"
                        :disabled="disabled"
                        v-model="campaign.type"
                        :campaignSegments="campaign?.segments || []"></campaign-step-form-audience-type>
                    <campaign-step-form-when
                        id="when"
                        ref="whenStepRef"
                        :disabled="disabled"
                        :campaignId="props.campaignId"
                        :campaignStatus="campaign.status"
                        :campaignStartedAt="campaign.started_at"
                        :originalLiveAt="campaign.original_live_at"
                        v-model:isRepeatable="campaign.is_repeatable"
                        v-model:durationType="campaign.duration_type"
                        v-model:liveAt="campaign.live_at"
                        v-model:timeZoneAware="campaign.time_zone_aware"></campaign-step-form-when>
                    <campaign-step-form-what
                        id="what"
                        :campaignId="campaign.id"
                        v-model="campaign.campaign_messages"></campaign-step-form-what>
                    <campaign-step-form-to
                        id="to"
                        :disabled="disabled"
                        :campaignId="campaign.id"
                        :campaignType="campaign.type"
                        :campaignDurationType="campaign.duration_type"
                        :campaignStatus="campaign.status"
                        :flowId="campaign?.flow_id"
                        v-model:originalCampaignId="campaign.original_campaign_id"
                        v-model:segmentIds="campaign.segments"
                        v-model:externalContactListIds="campaign.external_contact_lists"
                        v-model:everyone="campaign.everyone"
                        v-model:removeOnResponse="campaign.remove_on_response"
                        v-model:removeOnLinkClick="campaign.remove_on_link_click"
                        v-model:isRepeatable="campaign.is_repeatable"
                        v-model:repeatDelay="campaign.repeat_delay"
                        v-model:audiencePercentage="campaign.audience_percentage"
                        v-model:preferCorporateNumber="campaign.prefer_corporate_number"></campaign-step-form-to>

                        <vx-sticky-footer>
                            <router-button :to="{ name: 'campaigns' }"
                                class="w-20"
                                size="lg"
                                color="muted">
                                Cancel
                            </router-button>
                            <vx-button @click="handleSubmit()"
                                data-test="submit-button"
                                class="grow"
                                size="lg"
                                color="primary"
                                :loading="processing">
                                {{
                                    props.campaignId
                                        ? 'Update Campaign'
                                        : 'Create Campaign'
                                }}
                        </vx-button>
                    </vx-sticky-footer>
                </template>
            </form-wrapper>
    </div>

</template>

<script setup>
import { VxButton, VxStickyFooter, VxSwal } from '@voxie/frontend-components';
import StepFormHeading from '~/components/general/StepFormHeading.vue';
import { faFlagSwallowtail, faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import { computed, inject, nextTick, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import CampaignStepFormName from './CampaignStepFormName.vue';
import CampaignStepFormAudienceType from './CampaignStepFormAudienceType.vue';
import CampaignStepFormWhen from './CampaignStepFormWhen.vue';
import CampaignStepFormWhat from './CampaignStepFormWhat.vue';
import CampaignStepFormTo from './CampaignStepFormTo.vue';
import { useUrlSearchParams } from '@vueuse/core';
import { campaignsClient , flowsClient } from '~/services';
import FlowResourceStateAlert from '~/components/automation/flows/components/FlowResourceStateAlert.vue';
import { AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL, statuses as CampaignStatus } from '~/constants/campaign';
import dayjs, { toUTCDateTimeFormat } from '~/utils/dayjs';
import { formatTz, getClientTz, getTeamTz } from '~/utils/date';
import { maxLength, required } from '@vuelidate/validators';
import { displayFirstError } from '~/utils/validation';
import { clientTzToAmericaNewYorkUTC, UTCNewYorkToClientTz } from './timeZoneUtils';
import ResourceCategory from '~/components/general/categories/ResourceCategory.vue';
import FormWrapper from '~/components/general/FormWrapper.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import { useFormSubmission } from '~/composables/useFormSubmission';
import RouterButton from '~/components/general/RouterButton.vue';
import { HTTP_NOT_FOUND } from '~/support/HttpStatuses';

const teamId = Spark.state.currentTeam.id;

const props = defineProps({
    campaignId: {
        type: Number,
        required: false,
    },
});

const route = useRoute();
const router = useRouter();

const FEATURES = inject('FEATURES');

const whenStepRef = ref();
const flowAlertRef = ref();
const loading = ref(true);

const defaultCampaign = () => ({
    name: '',
    type: FEATURES.transactional_default ? AUDIENCE_TYPE_TRANSACTIONAL : AUDIENCE_TYPE_MARKETING,
    duration_type: '',
    live_at: new Date(),
    original_live_at: toUTCDateTimeFormat(dayjs()),
    original_campaign_id: null,
    segments: [],
    external_contact_lists: [],
    everyone: false,
    remove_on_response: false,
    remove_on_link_click: false,
    time_zone_aware: false,
    is_repeatable: false,
    repeat_delay: 10080,
    ...(FEATURES.campaign_filtering ? { audience_percentage: null } : {}),
    ...(FEATURES.hybrid_message_routing ? { prefer_corporate_number: true } : {}),
    campaign_messages: [],
    category: undefined,
});
const campaign = ref(defaultCampaign());
const savedCampaignId = ref();

const queryParams = useUrlSearchParams();

const disabled = computed(() => props.campaignId && ![CampaignStatus.DRAFT, CampaignStatus.SCHEDULED].includes(campaign.value.status));
const safeHoursTz = computed(() => campaign.value.time_zone_aware ? getClientTz() : getTeamTz());

const transformResponse = (data) => {
    const transformed = {
        ...data,
    }
    if (data.time_zone_aware) {
        transformed.live_at = UTCNewYorkToClientTz(data.live_at);
    } else {
        transformed.live_at = data.live_at ? dayjs.utc(data.live_at).toDate() : null;
    }

    transformed.original_live_at = data.live_at;

    return transformed;
}

const duplicate = async () => {

    const response = await campaignsClient.campaignGet(teamId, queryParams.duplicate_from_id);

    let flowSection;
    if (response.data.flow_id) {
        try {
            const { data: { config: { sections } } } = await flowsClient.getConfig(teamId, response.data.flow_id);
            flowSection = sections.findIndex(
                (section) => section.resources.find(
                    resource => resource.type === 'campaign' && Number(resource.id) === Number(queryParams.duplicate_from_id)
                )
            )
        } catch (e) {
            console.error(e)
        }
    }

    campaign.value = {
        ...transformResponse(response.data),
        id: undefined,
        name: '',
        flow_section: flowSection === -1 ? 0 : flowSection,
    }
}

onMounted(async () => {
    try {
        loading.value = true;
        if (props.campaignId) {
            const response = await campaignsClient.campaignGet(teamId, props.campaignId);
            campaign.value = transformResponse(response.data);
        }

        if (queryParams.duplicate_from_id) {
            await duplicate();
        }

        if (!campaign.value.flow_id && queryParams.flow_id) {
            campaign.value.flow_id = queryParams.flow_id;
            campaign.value.flow_section = Number(queryParams.flow_section || 0);
        }
    } catch (e) {
        console.error(e);

        if (e.response.status === HTTP_NOT_FOUND) {
            router.replace({ name: 'campaigns' });
        }
    } finally {
        await nextTick();
        loading.value = false;
    }
});

const saveData = async () => {
    const data = { ...campaign.value, segment_ids: [], external_contact_list_ids: [] };

    if (data.time_zone_aware) {
        // when time_zone_aware is set to true we send to the server as if the date was set in America/New_York
        data.live_at = clientTzToAmericaNewYorkUTC(data.live_at);
    } else {
        data.live_at = data.live_at ? toUTCDateTimeFormat(dayjs.tz(data.live_at, getClientTz())) : null;
    }

    data.segment_ids = data.segments.map(segment => segment.id);
    data.external_contact_list_ids = data.external_contact_lists.map(externalContactList => externalContactList.id);

    if (disabled.value) {
        delete data.segment_ids;
        delete data.external_contact_list_ids;
    }

    delete data.original_live_at;

    const response = await campaignsClient.campaignSave(teamId, data);
    savedCampaignId.value = response.data.id;
};

const { submit, processing, validation } = useFormSubmission({
    data: campaign,
    validation: {
        name: { required, maxLength: maxLength(180) },
    },
    preserveScroll: () => Boolean(props.campaignId),
    submit: saveData,
    reset: () => {
        if (!props.campaignId && !campaign.value.flow_id) {
            campaign.value = defaultCampaign();
        }
    },
    notification: () =>
        campaign.value.flow_id
            ? (
                props.campaignId
                    ? {
                        message: 'Campaign updated',
                        action: {
                            icon: faSquareArrowUpLeft,
                            label: 'Go to Flow',
                            href: flowAlertRef.value.flowLink,
                        },
                    }
                    : {
                        message: 'Campaign created',
                        redirect: flowAlertRef.value.flowLink,
                        action: {
                            icon: faSquareArrowUpLeft,
                            label: 'Edit Campaign',
                            href: `/messaging/campaigns/${savedCampaignId.value}`,
                        },
                    }
            )
            : (
                props.campaignId
                    ? {
                        message: 'Campaign updated',
                        action: {
                            icon: faSquareArrowUpLeft,
                            label: 'Back to Campaigns',
                            onClick: () => router.push({ name: 'campaigns', query: route.query }),
                        },
                    }
                    : {
                        message: 'Campaign created',
                        vueRedirect: { name: 'campaigns' },
                    }
            )
});

const handleSubmit = async () => {
    const startTime = getStartAtOutsideSafeHours();

    if (campaign.value.duration_type === 'one_time' && startTime !== null) {
        const formattedTimeZone = campaign.value.time_zone_aware
            ? 'local time'
            : formatTz(startTime.format('z'));

        const formattedStartTime = startTime.format('h:mm a');

        const html = `<div class="flex flex-col gap-3">
                <span>Your campaign is scheduled to start at <strong>${formattedStartTime} ${formattedTimeZone}</strong>, which could send messages outside of the safe sending hours of 8am to 9pm.</span>
                <span>This could lead to regulatory issues and increased opt-outs. We recommend scheduling between 10 am to 6 pm to drive the most engagement.</span>
            </div>`;

        await VxSwal.fire({
            icon: 'danger',
            title: 'Outside Safe Hours!',
            html: html,
            showCancelButton: true,
            confirmButtonColor: 'danger',
            confirmButtonText: 'Proceed anyway',
            preConfirm: () => submit(),
        });
    } else {
        await submit();
    }
};

const isLiveAtOutsideSafeHours = () => {
    const liveAt = dayjs(campaign.value.live_at).tz(safeHoursTz.value);

    return !liveAt.isBetween(
        liveAt.startOf('day').hour(8),
        liveAt.startOf('day').hour(20),
        'hour',
        '[)' // Inclusive start, exclusive end
    );
};

const getStartAtOutsideSafeHours = () => {
    if (!campaign.value.live_at) {
        return null;
    }

    if (isLiveAtOutsideSafeHours()) {
        return dayjs(campaign.value.live_at).tz(safeHoursTz.value);
    }

    const campaignMessage = campaign.value.campaign_messages.find((message) => {
        if (message.should_send_on_schedule) {
            const startDate = dayjs().tz(safeHoursTz.value).startOf('day');
            const messageStartAt = getMessageStartAt(message.send_at_time);

            return !messageStartAt.isBetween(
                startDate.hour(8),
                startDate.hour(20),
                'hour',
                '[)' // Inclusive start, exclusive end
            );
        }
    });

    return campaignMessage ? getMessageStartAt(campaignMessage.send_at_time) : null;
};

const getMessageStartAt = (sendAtTime) => {
    const startDate = dayjs().tz(safeHoursTz.value).startOf('day');
    const [hours, minutes] = sendAtTime.split(':').map(Number);

    return startDate.hour(hours).minute(minutes);
};

</script>
